import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, AlertCircle, Loader2, Search,
  TrendingUp, TrendingDown, Link2, Tag, RefreshCw, FileText, X as XIcon,
  Sparkles, Wand2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n, dir = null) => {
  const v = Number(n || 0).toFixed(2);
  return dir === 'debit' ? `-$${v}` : `$${v}`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

export const CrmBankImportClassify = () => {
  const { importId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({ expense: [], income: [], hst_rate: 0.13 });
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('unclassified'); // 'all' | 'unclassified' | 'income' | 'expense'
  const [search, setSearch] = useState('');
  const [matchFor, setMatchFor] = useState(null); // txn whose match dialog is open
  const [bulkOpen, setBulkOpen] = useState(false);
  const [smartOpen, setSmartOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [imp, cat] = await Promise.all([
        axios.get(`${API_URL}/api/admin/accounting/bank-imports/${importId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/accounting/bank-categories`, { withCredentials: true }),
      ]);
      setData(imp.data);
      setCategories(cat.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load import');
    } finally { setLoading(false); }
  }, [importId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const transactions = data?.transactions || [];
  const visible = useMemo(() => transactions.filter((t) => {
    if (filter === 'unclassified' && t.classification) return false;
    if (filter === 'income' && t.classification !== 'income') return false;
    if (filter === 'expense' && t.classification !== 'expense') return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(t.description || '').toLowerCase().includes(s)
        && !String(t.amount).includes(s)) return false;
    }
    return true;
  }), [transactions, filter, search]);

  const classifiedCount = transactions.filter((t) => !!t.classification).length;
  const pct = transactions.length > 0 ? Math.round((classifiedCount / transactions.length) * 100) : 0;
  const incomeTotal = transactions.filter((t) => t.classification === 'income').reduce((s, t) => s + Number(t.amount || 0), 0);
  const expenseTotal = transactions.filter((t) => t.classification === 'expense').reduce((s, t) => s + Number(t.amount || 0), 0);

  const toggleSelect = (txnId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId); else next.add(txnId);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (visible.every((t) => selected.has(t.bank_transaction_id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map((t) => t.bank_transaction_id)));
    }
  };

  const classifyRow = async (txnId, patch) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/accounting/bank-transactions/${txnId}`,
        patch, { withCredentials: true },
      );
      // Update local state without a full refetch
      setData((d) => ({
        ...d,
        transactions: d.transactions.map((t) =>
          t.bank_transaction_id === txnId ? { ...t, ...patch } : t,
        ),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-[#6B6B7A]">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading import…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="bank-classify-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <button
            onClick={() => navigate('/crm/accounting/bank-imports')}
            className="text-xs text-[#6B6B7A] hover:text-[#FF3B6B] mb-1 inline-flex items-center"
            data-testid="bank-classify-back"
          >
            <ArrowLeft className="w-3 h-3 mr-1" /> All imports
          </button>
          <h1 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {data.filename}
          </h1>
          <p className="text-sm text-[#6B6B7A]">
            {data.bank_name && <>{data.bank_name} · </>}
            {data.row_count} transactions{data.skipped_duplicate > 0 && <> · {data.skipped_duplicate} duplicates skipped</>}
          </p>
        </div>
        <Button
          variant="outline" onClick={fetchData}
          className="rounded-full border-[#E8E5DF]"
          data-testid="bank-classify-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Progress + summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
          <p className="text-xs text-[#6B6B7A] uppercase tracking-wider">Progress</p>
          <p className="text-2xl font-bold text-[#1E1E24]">{classifiedCount} / {data.row_count}</p>
          <div className="mt-2 h-2 bg-[#F0EDE6] rounded-full overflow-hidden">
            <div className="h-full bg-[#06D6A0] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-[#6B6B7A] mt-1">{pct}% classified</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
          <p className="text-xs text-[#06D6A0] uppercase tracking-wider flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Income</p>
          <p className="text-2xl font-bold text-[#06D6A0]">{fmt$(incomeTotal)}</p>
          <p className="text-[11px] text-[#6B6B7A]">{transactions.filter((t) => t.classification === 'income').length} transactions</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
          <p className="text-xs text-[#D1344D] uppercase tracking-wider flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Expense</p>
          <p className="text-2xl font-bold text-[#D1344D]">{fmt$(expenseTotal)}</p>
          <p className="text-[11px] text-[#6B6B7A]">{transactions.filter((t) => t.classification === 'expense').length} transactions</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
          <p className="text-xs text-[#6B6B7A] uppercase tracking-wider">Net effect</p>
          <p className={`text-2xl font-bold ${incomeTotal - expenseTotal >= 0 ? 'text-[#06D6A0]' : 'text-[#D1344D]'}`}>
            {fmt$(incomeTotal - expenseTotal)}
          </p>
          <p className="text-[11px] text-[#6B6B7A]">Across classified rows only</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center mb-4 bg-white rounded-2xl border border-[#E8E5DF] p-3">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
          <Input
            placeholder="Search description, amount…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white"
            data-testid="bank-classify-search"
          />
        </div>
        <div className="flex gap-1 bg-[#FFFDF8] p-1 rounded-full border border-[#E8E5DF]">
          {[
            { id: 'unclassified', label: 'To classify' },
            { id: 'income', label: 'Income' },
            { id: 'expense', label: 'Expense' },
            { id: 'all', label: 'All' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => { setFilter(f.id); setSelected(new Set()); }}
              data-testid={`bank-filter-${f.id}`}
              className={`px-3 py-1.5 text-xs rounded-full font-semibold transition ${
                filter === f.id ? 'bg-[#1E1E24] text-white' : 'text-[#6B6B7A] hover:bg-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <Button
          onClick={() => setSmartOpen(true)}
          variant="outline"
          className="rounded-full border-[#9333EA] text-[#9333EA] hover:bg-[#9333EA] hover:text-white"
          data-testid="bank-smart-categorize-open"
        >
          <Wand2 className="w-4 h-4 mr-1" /> Smart Auto-Categorize
        </Button>
        {selected.size > 0 && (
          <Button
            onClick={() => setBulkOpen(true)}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="bank-bulk-classify-open"
          >
            <Tag className="w-4 h-4 mr-1" /> Bulk classify {selected.size}
          </Button>
        )}
      </div>

      {/* Transactions table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-[#FFFDF8] sticky top-0">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={visible.length > 0 && visible.every((t) => selected.has(t.bank_transaction_id))}
                    onCheckedChange={toggleSelectAll}
                    data-testid="bank-classify-select-all"
                  />
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Classify</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">HST</TableHead>
                <TableHead>Match</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-[#6B6B7A]" data-testid="bank-classify-empty">
                  {filter === 'unclassified' ? (
                    <><CheckCircle2 className="w-8 h-8 text-[#06D6A0] mx-auto mb-2" /> Everything&apos;s classified! 🎉</>
                  ) : 'No transactions match the current filter.'}
                </TableCell></TableRow>
              ) : (
                visible.map((t) => (
                  <TxnRow
                    key={t.bank_transaction_id}
                    txn={t}
                    selected={selected.has(t.bank_transaction_id)}
                    onSelect={() => toggleSelect(t.bank_transaction_id)}
                    categories={categories}
                    onChange={(patch) => classifyRow(t.bank_transaction_id, patch)}
                    onOpenMatch={() => setMatchFor(t)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <BulkClassifyDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        selectedIds={Array.from(selected)}
        categories={categories}
        hstRate={categories.hst_rate}
        onDone={() => { setBulkOpen(false); setSelected(new Set()); fetchData(); }}
      />

      <SmartCategorizeDialog
        open={smartOpen}
        onOpenChange={setSmartOpen}
        importId={importId}
        unclassifiedCount={transactions.filter((t) => !t.classification).length}
        onApplied={() => { setSmartOpen(false); fetchData(); }}
      />

      <MatchBookingDialog
        txn={matchFor}
        open={!!matchFor}
        onOpenChange={(o) => !o && setMatchFor(null)}
        onMatched={(bookingId) => {
          classifyRow(matchFor.bank_transaction_id, { matched_booking_id: bookingId });
          setMatchFor(null);
        }}
      />
    </div>
  );
};

// ─────────── Single transaction row ───────────
const TxnRow = ({ txn, selected, onSelect, categories, onChange, onOpenMatch }) => {
  const opts = txn.classification === 'income' ? categories.income : categories.expense;
  const [editingCategory, setEditingCategory] = useState(false);
  const [newCat, setNewCat] = useState('');

  const handleClassifyChange = (v) => {
    if (v === '__none__') {
      onChange({ classification: null, category: null });
    } else {
      onChange({ classification: v });
    }
  };
  const handleCategoryChange = (v) => {
    if (v === '__add_new__') {
      setEditingCategory(true);
      setNewCat('');
    } else {
      onChange({ category: v });
    }
  };
  const submitNewCategory = () => {
    const c = newCat.trim();
    if (c) {
      onChange({ category: c });
      setEditingCategory(false);
      setNewCat('');
    }
  };

  return (
    <TableRow
      className={`${selected ? 'bg-[#FFF5F8]' : ''} ${txn.classification ? 'opacity-100' : ''}`}
      data-testid={`bank-txn-row-${txn.bank_transaction_id}`}
    >
      <TableCell><Checkbox checked={selected} onCheckedChange={onSelect} data-testid={`bank-txn-select-${txn.bank_transaction_id}`} /></TableCell>
      <TableCell className="text-xs text-[#6B6B7A] whitespace-nowrap">{fmtDate(txn.date)}</TableCell>
      <TableCell className="max-w-[260px]">
        <p className="text-sm text-[#1E1E24] truncate" title={txn.description}>{txn.description}</p>
        {txn.matched_booking_id && (
          <p className="text-[10px] text-[#06D6A0] mt-0.5 flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Matched · {txn.matched_booking_id.slice(0, 16)}
          </p>
        )}
      </TableCell>
      <TableCell className={`text-right font-semibold whitespace-nowrap ${txn.direction === 'credit' ? 'text-[#06D6A0]' : 'text-[#D1344D]'}`}>
        {fmt$(txn.amount, txn.direction)}
      </TableCell>
      <TableCell>
        <Select
          value={txn.classification || '__none__'}
          onValueChange={handleClassifyChange}
        >
          <SelectTrigger className="h-8 w-[120px] text-xs bg-white" data-testid={`bank-txn-class-${txn.bank_transaction_id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Unclassified</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="min-w-[180px]">
        {editingCategory ? (
          <div className="flex gap-1">
            <Input
              autoFocus
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitNewCategory()}
              placeholder="New category…"
              className="h-8 text-xs"
              data-testid={`bank-txn-newcat-${txn.bank_transaction_id}`}
            />
            <Button size="sm" onClick={submitNewCategory} className="h-8 px-2"><CheckCircle2 className="w-3 h-3" /></Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingCategory(false)} className="h-8 px-2"><XIcon className="w-3 h-3" /></Button>
          </div>
        ) : (
          <Select
            value={txn.category || '__none__'}
            onValueChange={handleCategoryChange}
            disabled={!txn.classification}
          >
            <SelectTrigger className="h-8 text-xs bg-white" data-testid={`bank-txn-cat-${txn.bank_transaction_id}`}>
              <SelectValue placeholder={txn.classification ? 'Pick category…' : '—'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {opts.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
              <SelectItem value="__add_new__">+ Add new category…</SelectItem>
            </SelectContent>
          </Select>
        )}
      </TableCell>
      <TableCell className="text-right w-[110px]">
        <HstCell txn={txn} onChange={onChange} />
      </TableCell>
      <TableCell>
        <Button
          size="sm"
          variant={txn.matched_booking_id ? 'default' : 'ghost'}
          onClick={onOpenMatch}
          className="h-7 text-xs"
          data-testid={`bank-txn-match-${txn.bank_transaction_id}`}
        >
          <Link2 className="w-3 h-3 mr-1" />
          {txn.matched_booking_id ? 'Linked' : 'Match'}
        </Button>
      </TableCell>
    </TableRow>
  );
};

// ─────────── HST cell with inline auto-calc ───────────
const HstCell = ({ txn, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(txn.hst_amount != null ? String(txn.hst_amount) : '');
  const rate = 0.13;
  const autoCalc = () => {
    const auto = (Number(txn.amount || 0) * (rate / (1 + rate))).toFixed(2);
    setVal(auto);
    onChange({ hst_amount: Number(auto) });
    setEditing(false);
  };
  const save = () => {
    const n = val === '' ? null : Number(val);
    if (n != null && (Number.isNaN(n) || n < 0)) {
      setEditing(false);
      return;
    }
    onChange({ hst_amount: n });
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="flex gap-1 items-center">
        <Input
          autoFocus type="number" step="0.01" min="0"
          value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          onBlur={save}
          className="h-7 text-xs w-20 text-right"
          data-testid={`bank-txn-hst-input-${txn.bank_transaction_id}`}
        />
        <button
          type="button" onClick={autoCalc} title="Auto-calc 13% HST (HST-inclusive)"
          className="text-[10px] text-[#FF3B6B] hover:underline"
          data-testid={`bank-txn-hst-auto-${txn.bank_transaction_id}`}
        ><Sparkles className="w-3 h-3" /></button>
      </div>
    );
  }
  return (
    <button
      type="button" onClick={() => { setVal(txn.hst_amount != null ? String(txn.hst_amount) : ''); setEditing(true); }}
      disabled={!txn.classification}
      className={`text-xs ${txn.hst_amount != null ? 'text-[#1E1E24] font-semibold' : 'text-[#6B6B7A] hover:text-[#FF3B6B]'} disabled:opacity-30`}
      data-testid={`bank-txn-hst-${txn.bank_transaction_id}`}
    >
      {txn.hst_amount != null ? `$${Number(txn.hst_amount).toFixed(2)}` : '— add'}
    </button>
  );
};

// ─────────── Bulk classify dialog ───────────
const BulkClassifyDialog = ({ open, onOpenChange, selectedIds, categories, hstRate, onDone }) => {
  const [classification, setClassification] = useState('expense');
  const [category, setCategory] = useState('__keep__');
  const [autoHst, setAutoHst] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setClassification('expense'); setCategory('__keep__'); setAutoHst(false); }
  }, [open]);

  const opts = classification === 'income' ? categories.income : categories.expense;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        transaction_ids: selectedIds,
        classification,
        category: (category === '__keep__' || category === '__none__') ? null : category,
        auto_hst: autoHst,
      };
      const r = await axios.post(
        `${API_URL}/api/admin/accounting/bank-transactions/bulk-classify`,
        payload, { withCredentials: true },
      );
      toast.success(`Updated ${r.data.updated} transaction${r.data.updated === 1 ? '' : 's'}`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk classify failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="bank-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Bulk classify {selectedIds.length} transactions</DialogTitle>
          <DialogDescription className="text-xs">
            Apply the same classification + category to every selected row.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Classify as</Label>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger className="mt-1 bg-white" data-testid="bank-bulk-class"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category (optional)</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1 bg-white" data-testid="bank-bulk-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__keep__">— Keep existing —</SelectItem>
                <SelectItem value="__none__">— Clear category —</SelectItem>
                {opts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {classification === 'expense' && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox" checked={autoHst} onChange={(e) => setAutoHst(e.target.checked)}
                className="rounded border-[#E8E5DF]"
                data-testid="bank-bulk-auto-hst"
              />
              Auto-calculate {(hstRate * 100).toFixed(0)}% HST (HST-inclusive)
            </label>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit} disabled={busy}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="bank-bulk-apply"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Apply to {selectedIds.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────── Booking-match dialog ───────────
const MatchBookingDialog = ({ txn, open, onOpenChange, onMatched }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualBookingId, setManualBookingId] = useState('');

  useEffect(() => {
    if (open && txn) {
      setManualBookingId('');
      setLoading(true);
      axios.get(
        `${API_URL}/api/admin/accounting/bank-transactions/${txn.bank_transaction_id}/match-suggestions`,
        { withCredentials: true },
      ).then((r) => setSuggestions(r.data?.items || []))
        .catch(() => setSuggestions([]))
        .finally(() => setLoading(false));
    }
  }, [open, txn]);

  if (!txn) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="bank-match-dialog">
        <DialogHeader>
          <DialogTitle>Match to a booking</DialogTitle>
          <DialogDescription className="text-xs">
            {txn.description} · {fmt$(txn.amount, txn.direction)} · {fmtDate(txn.date)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {loading ? (
            <p className="text-center text-sm text-[#6B6B7A] py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Finding matches…</p>
          ) : suggestions.length === 0 ? (
            <p className="text-center text-sm text-[#6B6B7A] py-6">No bookings match within ±30 days and ±2%</p>
          ) : (
            suggestions.map((b) => (
              <button
                key={`${b.booking_id}-${b.match_kind}`}
                onClick={() => onMatched(b.booking_id)}
                className="w-full text-left bg-white border border-[#E8E5DF] rounded-xl p-3 hover:border-[#FF3B6B] hover:bg-[#FFFDF8]"
                data-testid={`bank-match-${b.booking_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-[#1E1E24]">{b.child_name || '—'} <span className="font-normal text-[#6B6B7A]">· {b.customer_name}</span></p>
                    <p className="text-xs text-[#6B6B7A]">{b.date} · {b.booking_id.slice(0, 16)}…</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">${b.match_amount?.toFixed(2)}</p>
                    <Badge variant="outline" className="text-[10px]">{b.match_kind}</Badge>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        <div className="pt-3 border-t border-[#E8E5DF]">
          <Label className="text-xs">Or paste a booking ID manually</Label>
          <div className="flex gap-1 mt-1">
            <Input
              value={manualBookingId}
              onChange={(e) => setManualBookingId(e.target.value)}
              placeholder="booking_…"
              data-testid="bank-match-manual"
            />
            <Button
              onClick={() => onMatched(manualBookingId.trim())}
              disabled={!manualBookingId.trim()}
              data-testid="bank-match-manual-apply"
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            >Match</Button>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {txn.matched_booking_id && (
            <Button
              variant="ghost"
              onClick={() => onMatched('')}
              className="text-[#D1344D]"
              data-testid="bank-match-unlink"
            >Unlink</Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────── Smart auto-categorize dialog (LLM-powered) ───────────
const SmartCategorizeDialog = ({ open, onOpenChange, importId, unclassifiedCount, onApplied }) => {
  const [phase, setPhase] = useState('intro'); // 'intro' | 'thinking' | 'review' | 'applying'
  const [suggestions, setSuggestions] = useState([]);
  const [minConfidence, setMinConfidence] = useState(0.7);
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [considered, setConsidered] = useState(0);
  const [overrides, setOverrides] = useState({});  // { [txnId]: { accept: true|false } }

  useEffect(() => {
    if (!open) {
      // Reset state on close
      setPhase('intro');
      setSuggestions([]);
      setMinConfidence(0.7);
      setOverrides({});
    }
  }, [open]);

  const runSuggest = async () => {
    setPhase('thinking');
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/accounting/bank-imports/${importId}/auto-categorize`,
        { apply: false, only_unclassified: true, min_confidence: minConfidence, limit: 200 },
        { withCredentials: true },
      );
      setSuggestions(r.data?.suggestions || []);
      setConsidered(r.data?.considered || 0);
      setModel(r.data?.model || 'claude-sonnet-4-6');
      // Default each suggestion's accept flag based on confidence vs threshold
      const init = {};
      (r.data?.suggestions || []).forEach((s) => {
        init[s.bank_transaction_id] = { accept: s.confidence >= minConfidence };
      });
      setOverrides(init);
      setPhase('review');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI suggestion failed');
      setPhase('intro');
    }
  };

  const acceptedIds = useMemo(
    () => suggestions.filter((s) => overrides[s.bank_transaction_id]?.accept).map((s) => s.bank_transaction_id),
    [suggestions, overrides],
  );

  const applyAccepted = async () => {
    setPhase('applying');
    let applied = 0;
    try {
      // Patch each accepted row with its suggested (classification, category)
      // Run as a sequential await loop to keep error messages clean.
      for (const s of suggestions) {
        if (!overrides[s.bank_transaction_id]?.accept) continue;
        try {
          await axios.patch(
            `${API_URL}/api/admin/accounting/bank-transactions/${s.bank_transaction_id}`,
            { classification: s.classification, category: s.category || null },
            { withCredentials: true },
          );
          applied += 1;
        } catch (rowErr) {
          // skip — surface count at the end
        }
      }
      toast.success(`Applied ${applied} of ${acceptedIds.length} suggestion${acceptedIds.length === 1 ? '' : 's'}`);
      onApplied?.();
    } catch (e) {
      toast.error('Apply failed — some rows may have been saved');
      onApplied?.();
    }
  };

  const toggleOverride = (id) => {
    setOverrides((prev) => ({ ...prev, [id]: { accept: !prev[id]?.accept } }));
  };
  const selectAllConf = () => {
    const next = {};
    suggestions.forEach((s) => { next[s.bank_transaction_id] = { accept: s.confidence >= minConfidence }; });
    setOverrides(next);
  };

  const confidenceBucket = (c) => {
    if (c >= 0.9) return { label: 'High', cls: 'bg-[#E6F9F0] text-[#06A07A]' };
    if (c >= 0.65) return { label: 'Med', cls: 'bg-[#FFF7EB] text-[#B8860B]' };
    return { label: 'Low', cls: 'bg-[#FFE4E1] text-[#D1344D]' };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="bank-smart-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#9333EA]" />
            Smart Auto-Categorize
          </DialogTitle>
          <DialogDescription className="text-xs">
            Uses Claude Sonnet 4.6 to pre-fill the classification + category for every
            un-classified transaction. You review and accept before anything is saved.
          </DialogDescription>
        </DialogHeader>

        {phase === 'intro' && (
          <div className="space-y-4">
            <div className="bg-[#F4ECFF] border border-[#9333EA]/30 rounded-xl p-4">
              <p className="text-sm text-[#1E1E24]">
                <strong>{unclassifiedCount}</strong> un-classified transaction{unclassifiedCount === 1 ? '' : 's'} will
                be sent to the AI. It returns a suggestion + confidence score for each row —
                nothing is saved until you click <em>Apply</em> on the next screen.
              </p>
            </div>
            <div className="text-xs text-[#6B6B7A] space-y-1">
              <p>· Uses your Emergent Universal LLM key (deducted from your balance).</p>
              <p>· Only the description + amount + direction is sent — no customer data.</p>
              <p>· Roughly 1 cent per 100 transactions.</p>
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={runSuggest}
                disabled={unclassifiedCount === 0}
                className="rounded-full bg-[#9333EA] hover:bg-[#7d2bce] text-white"
                data-testid="bank-smart-suggest-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Suggest classifications
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'thinking' && (
          <div className="py-10 text-center" data-testid="bank-smart-thinking">
            <Loader2 className="w-8 h-8 animate-spin text-[#9333EA] mx-auto mb-3" />
            <p className="text-sm text-[#1E1E24] font-semibold">Analyzing your transactions…</p>
            <p className="text-xs text-[#6B6B7A] mt-1">Usually takes 4–10 seconds.</p>
          </div>
        )}

        {phase === 'review' && (
          <div className="space-y-3" data-testid="bank-smart-review">
            <div className="flex items-center justify-between bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3 gap-3">
              <div className="text-xs">
                <p className="font-semibold text-[#1E1E24]">{considered} transactions analysed</p>
                <p className="text-[#6B6B7A]">
                  {suggestions.filter((s) => s.confidence >= 0.9).length} high · {' '}
                  {suggestions.filter((s) => s.confidence >= 0.65 && s.confidence < 0.9).length} medium · {' '}
                  {suggestions.filter((s) => s.confidence < 0.65).length} low confidence
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Auto-accept ≥</Label>
                <Input
                  type="number" step="0.05" min="0" max="1"
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(Number(e.target.value))}
                  className="w-16 h-8 text-xs text-right"
                  data-testid="bank-smart-confidence"
                />
                <Button size="sm" variant="outline" onClick={selectAllConf} className="text-xs h-8" data-testid="bank-smart-reselect">
                  Re-select
                </Button>
              </div>
            </div>

            <div className="border border-[#E8E5DF] rounded-xl overflow-hidden max-h-[420px] overflow-y-auto">
              <Table>
                <TableHeader className="bg-[#FFFDF8] sticky top-0">
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={acceptedIds.length === suggestions.length && suggestions.length > 0}
                        onCheckedChange={() => {
                          const allAccepted = acceptedIds.length === suggestions.length;
                          const next = {};
                          suggestions.forEach((s) => { next[s.bank_transaction_id] = { accept: !allAccepted }; });
                          setOverrides(next);
                        }}
                        data-testid="bank-smart-select-all"
                      />
                    </TableHead>
                    <TableHead>Suggestion</TableHead>
                    <TableHead className="w-24">Confidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suggestions.map((s) => {
                    const bucket = confidenceBucket(s.confidence);
                    const accepted = !!overrides[s.bank_transaction_id]?.accept;
                    return (
                      <TableRow
                        key={s.bank_transaction_id}
                        className={accepted ? 'bg-[#F4ECFF]' : ''}
                        data-testid={`bank-smart-row-${s.bank_transaction_id}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={accepted}
                            onCheckedChange={() => toggleOverride(s.bank_transaction_id)}
                            data-testid={`bank-smart-accept-${s.bank_transaction_id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <Badge
                              variant="outline"
                              className={s.classification === 'income'
                                ? 'border-[#06D6A0] text-[#06A07A]'
                                : 'border-[#D1344D] text-[#D1344D]'}
                            >
                              {s.classification}
                            </Badge>
                            <span className="font-semibold text-[#1E1E24]">{s.category || <span className="text-[#6B6B7A] italic">(no category)</span>}</span>
                          </div>
                          {s.reason && <p className="text-[10px] text-[#6B6B7A] mt-0.5 truncate">{s.reason}</p>}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${bucket.cls}`}>
                            {(s.confidence * 100).toFixed(0)}% {bucket.label}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <p className="text-[11px] text-[#6B6B7A]">
              Powered by {model}. Uncheck any row you&apos;d rather classify by hand.
            </p>

            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={applyAccepted}
                disabled={acceptedIds.length === 0}
                className="rounded-full bg-[#9333EA] hover:bg-[#7d2bce] text-white"
                data-testid="bank-smart-apply-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Apply {acceptedIds.length} suggestion{acceptedIds.length === 1 ? '' : 's'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'applying' && (
          <div className="py-10 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#9333EA] mx-auto mb-3" />
            <p className="text-sm text-[#1E1E24] font-semibold">Saving suggestions…</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CrmBankImportClassify;
